import React, { useEffect, useState, useRef, useCallback } from "react";
import { useRouter } from "next/router";
import Header from "components/Header/Header";
import ActingAsBanner from "components/ActAs/ActingAsBanner";
import DesktopSidebar from "components/DesktopSidebar/DesktopSidebar";
import MobileBottomNav from "components/MobileBottomNav/MobileBottomNav";
import Footer from "components/Footer/Footer";
import { useMobileHeader } from "hocs/MobileHeaderContext";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import { SidebarProvider } from "hocs/SidebarContext";
import RouteProgress from "./RouteProgress";

/**
 * Persistent app shell — lives in _app.js, never remounts.
 * Renders the Header, bottom nav, and page content area.
 * Individual pages no longer need to render Header or BottomNav.
 */
const AppShell = ({ children }) => {
  const router = useRouter();
  const { mobileBack } = useMobileHeader();
  const [transitionClass, setTransitionClass] = useState("");
  const directionRef = useRef("forward");

  // Track navigation direction for page slide transitions
  const handleRouteChangeStart = useCallback(() => {
    directionRef.current = mobileBack ? "back" : "forward";
    setTransitionClass("");
  }, [mobileBack]);

  const timerRef = useRef(null);

  const handleRouteChangeComplete = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const dir = directionRef.current;
    setTransitionClass(`page-slide-${dir}-enter`);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTransitionClass(`page-slide-${dir}-enter page-slide-${dir}-enter-active`);
      });
    });
    timerRef.current = setTimeout(() => setTransitionClass(""), 400);
  }, []);

  useEffect(() => {
    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router.events, handleRouteChangeStart, handleRouteChangeComplete]);

  return (
    <SidebarProvider>
      <RouteProgress />
      <div className="h-screen bg-muted lg:p-1">
        <div className="h-full flex overflow-hidden lg:rounded-lg lg:border lg:border-border/40 lg:shadow-sm">
          <ErrorBoundary>
            <DesktopSidebar />
          </ErrorBoundary>
          <div className="flex flex-col w-0 flex-1 overflow-hidden bg-background">
            <ErrorBoundary>
              <Header />
            </ErrorBoundary>
            <ErrorBoundary>
              <ActingAsBanner />
            </ErrorBoundary>
            <div
              className={`flex flex-col flex-1 relative focus:outline-none bg-muted ${
                router.asPath === "/valuation/list" ? "overflow-y-scroll" : "overflow-y-auto"
              }`}
            >
              <ErrorBoundary>
                <div className={`flex-1 flex flex-col ${transitionClass}`}>
                  {children}
                </div>
                {/* Spacer so content isn't hidden behind the floating bottom nav */}
                <div className="lg:hidden h-24" />
              </ErrorBoundary>
              {/* Inside the scroll container, after the flex-1 content: sits at
                  the bottom of short pages but scrolls away on long ones, so it
                  never covers the last row of a list. */}
              <Footer />
            </div>
            <MobileBottomNav />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AppShell;
